import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { toast } from 'react-toastify';
import { Plus, Edit2, Trash2, Calendar, AlertCircle, CheckCircle } from 'lucide-react';

const Tasks = () => {
  const [tasks, setTasks] = useState([]);
  const [retailers, setRetailers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [formData, setFormData] = useState({
    retailer_id: '',
    title: '',
    description: '',
    due_date: '',
    status: 'pending',
    priority: 'medium'
  });

  const taskStatuses = ['pending', 'in_progress', 'completed'];
  const taskPriorities = ['low', 'medium', 'high'];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [tasksRes, retailersRes] = await Promise.all([
        api.get('/tasks'),
        api.get('/retailers')
      ]);
      setTasks(tasksRes.data);
      setRetailers(retailersRes.data);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to fetch tasks');
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTask) {
        await api.put(`/tasks/${editingTask.id}`, formData);
        toast.success('Task updated successfully');
      } else {
        await api.post('/tasks', formData);
        toast.success('Task created successfully');
      }
      fetchData();
      resetForm();
    } catch (error) {
      toast.error(editingTask ? 'Failed to update task' : 'Failed to create task');
    }
  };

  const handleEdit = (task) => {
    setEditingTask(task);
    setFormData(task);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this task?')) {
      try {
        await api.delete(`/tasks/${id}`);
        toast.success('Task deleted successfully');
        fetchData();
      } catch (error) {
        toast.error('Failed to delete task');
      }
    }
  };

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      await api.put(`/tasks/${taskId}`, { status: newStatus });
      toast.success('Task status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update task status');
    }
  };

  const resetForm = () => {
    setShowModal(false);
    setEditingTask(null);
    setFormData({
      retailer_id: '',
      title: '',
      description: '',
      due_date: '',
      status: 'pending',
      priority: 'medium'
    });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const filteredTasks = filterStatus === 'all' 
    ? tasks 
    : tasks.filter(task => task.status === filterStatus);

  const getPriorityColor = (priority) => {
    const colors = {
      low: '#28a745',
      medium: '#ffc107',
      high: '#dc3545'
    };
    return colors[priority] || '#6c757d';
  };

  const getStatusIcon = (status) => {
    if (status === 'completed') return <CheckCircle size={16} style={{ color: '#28a745' }} />;
    if (status === 'in_progress') return <AlertCircle size={16} style={{ color: '#ffc107' }} />;
    return <Calendar size={16} style={{ color: '#6c757d' }} />;
  };

  const isOverdue = (dueDate) => {
    return new Date(dueDate) < new Date() && dueDate;
  };

  if (loading) {
    return <div className="container">Loading tasks...</div>;
  }

  return (
    <div className="container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <h1>Tasks</h1>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>
          <Plus size={16} style={{ marginRight: '5px' }} />
          Add Task
        </button>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <button
          className={`btn ${filterStatus === 'all' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setFilterStatus('all')}
          style={{ marginRight: '10px' }}
        >
          All Tasks ({tasks.length})
        </button>
        <button
          className={`btn ${filterStatus === 'pending' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setFilterStatus('pending')}
          style={{ marginRight: '10px' }}
        >
          Pending ({tasks.filter(t => t.status === 'pending').length})
        </button>
        <button
          className={`btn ${filterStatus === 'in_progress' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setFilterStatus('in_progress')}
          style={{ marginRight: '10px' }}
        >
          In Progress ({tasks.filter(t => t.status === 'in_progress').length})
        </button>
        <button
          className={`btn ${filterStatus === 'completed' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setFilterStatus('completed')}
        >
          Completed ({tasks.filter(t => t.status === 'completed').length})
        </button>
      </div>

      <div className="card">
        <table className="table">
          <thead>
            <tr>
              <th>Status</th>
              <th>Title</th>
              <th>Retailer</th>
              <th>Priority</th>
              <th>Due Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredTasks.map(task => (
              <tr key={task.id} style={{ opacity: task.status === 'completed' ? 0.6 : 1 }}>
                <td>
                  <select
                    value={task.status}
                    onChange={(e) => handleStatusChange(task.id, e.target.value)}
                    style={{
                      border: 'none',
                      background: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    {taskStatuses.map(status => (
                      <option key={status} value={status}>
                        {status.replace('_', ' ')}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    {getStatusIcon(task.status)}
                    <div style={{ marginLeft: '8px' }}>
                      <strong>{task.title}</strong>
                      {task.description && (
                        <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                          {task.description}
                        </div>
                      )}
                    </div>
                  </div>
                </td>
                <td>{task.retailer_name || 'General'}</td>
                <td>
                  <span style={{
                    display: 'inline-block',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: '500',
                    color: 'white',
                    backgroundColor: getPriorityColor(task.priority)
                  }}>
                    {task.priority}
                  </span>
                </td>
                <td>
                  {task.due_date && (
                    <span style={{ color: isOverdue(task.due_date) && task.status !== 'completed' ? '#dc3545' : 'inherit' }}>
                      {new Date(task.due_date).toLocaleDateString()}
                      {isOverdue(task.due_date) && task.status !== 'completed' && (
                        <span className="badge badge-danger" style={{ marginLeft: '8px' }}>
                          Overdue
                        </span>
                      )}
                    </span>
                  )}
                </td>
                <td>
                  <button
                    onClick={() => handleEdit(task)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', marginRight: '10px' }}
                  >
                    <Edit2 size={16} />
                  </button>
                  <button
                    onClick={() => handleDelete(task.id)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc3545' }}
                  >
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={resetForm}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">
                {editingTask ? 'Edit Task' : 'Create New Task'}
              </h2>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">Title *</label>
                <input
                  type="text"
                  name="title"
                  className="form-control"
                  value={formData.title}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea
                  name="description"
                  className="form-control"
                  value={formData.description}
                  onChange={handleChange}
                  rows="3"
                />
              </div>

              <div className="grid grid-2">
                <div className="form-group">
                  <label className="form-label">Retailer</label>
                  <select
                    name="retailer_id"
                    className="form-control"
                    value={formData.retailer_id}
                    onChange={handleChange}
                  >
                    <option value="">General Task</option>
                    {retailers.map(retailer => (
                      <option key={retailer.id} value={retailer.id}>
                        {retailer.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Priority</label>
                  <select
                    name="priority"
                    className="form-control"
                    value={formData.priority}
                    onChange={handleChange}
                  >
                    {taskPriorities.map(priority => (
                      <option key={priority} value={priority}>
                        {priority.charAt(0).toUpperCase() + priority.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Due Date</label>
                  <input
                    type="date"
                    name="due_date"
                    className="form-control"
                    value={formData.due_date}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select
                    name="status"
                    className="form-control"
                    value={formData.status}
                    onChange={handleChange}
                  >
                    {taskStatuses.map(status => (
                      <option key={status} value={status}>
                        {status.replace('_', ' ').charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={resetForm}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingTask ? 'Update' : 'Create'} Task
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tasks;